import { useState, useEffect, useMemo } from 'react';
import { baseSupabase } from '@/integrations/supabase/client';
import type { ContestState, ContestUIState } from '@/types/founding100Contest';

// ⚡ V325.5: SessionStorage cache to prevent "Loading contest..." on remount
const CONTEST_CACHE_KEY = 'bn:contest:state';
const ONE_HOUR_MS = 60 * 60 * 1000;

function readContestCache(): ContestState | null {
  try {
    const raw = sessionStorage.getItem(CONTEST_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - (cached.timestamp || 0) > ONE_HOUR_MS) return null;
    return cached.data as ContestState;
  } catch { return null; }
}

function writeContestCache(data: ContestState): void {
  try {
    sessionStorage.setItem(CONTEST_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {}
}

interface UseFoundingContestStateResult {
  contestState: ContestState | null;
  uiState: ContestUIState | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage Founding 100 contest state
 *
 * V325.5: Now uses sessionStorage cache with 1h TTL.
 * On remount, returns cached data instantly (isLoading=false).
 * Background-refreshes only when cache is stale (>1h).
 */
export function useFoundingContestState(): UseFoundingContestStateResult {
  // V325.5: Initialize from cache for instant render on remount
  const [contestState, setContestState] = useState<ContestState | null>(() => readContestCache());
  const [isLoading, setIsLoading] = useState(() => readContestCache() === null);
  const [error, setError] = useState<Error | null>(null);

  const fetchContestState = async (forceRefresh = false) => {
    // V325.5: TTL check — skip RPC if cache is fresh (unless forced)
    if (!forceRefresh) {
      const cached = readContestCache();
      if (cached) {
        setContestState(cached);
        setIsLoading(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: rpcError } = await baseSupabase.rpc('get_founding_contest_state');

      if (rpcError) {
        console.error('❌ Contest state RPC error:', rpcError);
        throw new Error(rpcError.message || 'Failed to fetch contest state');
      }

      if (!data) {
        throw new Error('No contest state data returned');
      }

      if (import.meta.env.DEV) console.log('✅ V325.5: Contest state loaded (fresh)');
      setContestState(data as ContestState);
      writeContestCache(data as ContestState);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching contest state');
      console.error('❌ Contest state fetch error:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on mount (will skip if cache is fresh)
  useEffect(() => {
    void fetchContestState();
  }, []);

  // Derive UI state from contest state
  const uiState = useMemo<ContestUIState | null>(() => {
    if (!contestState) return null;

    const isPrelaunch = contestState.phase === 'prelaunch';
    const isRunning = contestState.phase === 'running';
    const isClosed = contestState.phase === 'closed';

    const progressPercent = Math.min(
      100,
      Math.round((contestState.applicationsCount / contestState.threshold) * 100)
    );

    const remainingApps = Math.max(0, contestState.threshold - contestState.applicationsCount);

    const hasApplied = !!contestState.userApplication;
    const isPending = contestState.userApplication?.status === 'pending';
    const isApproved = contestState.userApplication?.status === 'active';
    const isWaitlist = contestState.userApplication?.status === 'waitlist';
    const isRejected = contestState.userApplication?.status === 'rejected';

    const userRank = contestState.userApplication?.winnerRank ?? null;
    const isWinner = userRank !== null && userRank >= 1 && userRank <= 100;

    return {
      isPrelaunch,
      isRunning,
      isClosed,
      progressPercent,
      remainingApps,
      hasApplied,
      isPending,
      isApproved,
      isWaitlist,
      isRejected,
      isWinner,
      userRank,
    };
  }, [contestState]);

  return {
    contestState,
    uiState,
    isLoading,
    error,
    refetch: () => fetchContestState(true), // V325.5: Force refresh bypasses cache
  };
}

